import { Challenge } from './challenges.types'

const INSTRUCTIONS_CSHARP = `# Connect a Remote Service with Graftcode

Call a remote method as if it were local — strongly typed, no HTTP boilerplate.

---

## 1. Create a .NET project

\`\`\`bash
dotnet new console -n EnergyConsumer
cd EnergyConsumer
\`\`\`

## 2. Install the Graft

\`\`\`bash
dotnet add package -s https://grft.dev/4b4e411f-60a0-4868-b8a6-46f5dee07448__free graft.nuget.energypriceservice --version 1.2.0
\`\`\`

## 3. Replace \`Program.cs\` and run

Open \`Program.cs\` in the file explorer and replace its contents with:

\`\`\`csharp
using graft.nuget.EnergyPriceService;
GraftConfig.Host = "wss://gc-d-ca-polc-demo-ecbe-01.blackgrass-d2c29aae.polandcentral.azurecontainerapps.io/ws";

var consumption = MeterLogic.NetConsumptionKWh(1000, 1150);
Console.WriteLine($"Net consumption: {consumption}");
\`\`\`

Then run:

\`\`\`bash
dotnet run
\`\`\`

You should see the net consumption value. \`MeterLogic.NetConsumptionKWh(...)\` is a remote call that reads like a local method — no HTTP, no serialization.`

const INSTRUCTIONS_JAVA = `# Connect a Remote Service with Graftcode

Call a remote method as if it were local — strongly typed, no HTTP boilerplate.

---

## 1. Create a project folder

\`\`\`bash
mkdir java-energy-consumer
cd java-energy-consumer
\`\`\`

## 2. Create \`pom.xml\`

Create the file:

\`\`\`bash
touch pom.xml
\`\`\`

Open \`pom.xml\` in the file explorer and paste:

\`\`\`xml
<?xml version="1.0" encoding="UTF-8"?>
<project xmlns="http://maven.apache.org/POM/4.0.0"
         xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
         xsi:schemaLocation="http://maven.apache.org/POM/4.0.0 http://maven.apache.org/xsd/maven-4.0.0.xsd">
    <modelVersion>4.0.0</modelVersion>
    <groupId>energy</groupId>
    <artifactId>java-energy-consumer</artifactId>
    <version>1.0-SNAPSHOT</version>
    <packaging>jar</packaging>
    <properties>
        <maven.compiler.source>17</maven.compiler.source>
        <maven.compiler.target>17</maven.compiler.target>
    </properties>
    <repositories>
        <repository>
            <id>graft-repository</id>
            <url>https://grft.dev/maven2/4b4e411f-60a0-4868-b8a6-46f5dee07448__free</url>
        </repository>
    </repositories>
    <dependencies>
        <dependency>
            <groupId>graft.nuget</groupId>
            <artifactId>energypriceservice</artifactId>
            <version>1.2.0</version>
        </dependency>
    </dependencies>
</project>
\`\`\`

## 3. Create \`Main.java\` and run

Create the directory and file:

\`\`\`bash
mkdir -p src/main/java/energy
touch src/main/java/energy/Main.java
\`\`\`

Open \`Main.java\` in the file explorer and paste:

\`\`\`java
package energy;

import graft.nuget.EnergyPriceService.GraftConfig;
import graft.nuget.EnergyPriceService.EnergyPriceService.MeterLogic;

public class Main {
    public static void main(String[] args) throws Exception {
        GraftConfig.host = "wss://gc-d-ca-polc-demo-ecbe-01.blackgrass-d2c29aae.polandcentral.azurecontainerapps.io/ws";

        int consumption = MeterLogic.netConsumptionKWh(1000, 1150);
        System.out.println("Net consumption: " + consumption);
    }
}
\`\`\`

Then run:

\`\`\`bash
mvn compile exec:java "-Dexec.mainClass=energy.Main"
\`\`\`

You should see the net consumption value. \`MeterLogic.netConsumptionKWh(...)\` is a remote call that reads like a local method — no HTTP, no serialization.`

const INSTRUCTIONS_JAVASCRIPT = `# Connect a Remote Service with Graftcode

Call a remote method as if it were local — strongly typed, no HTTP boilerplate.

---

## 1. Create a Node.js project

\`\`\`bash
mkdir js-energy-consumer
cd js-energy-consumer
npm init -y
\`\`\`

## 2. Install the Graft

\`\`\`bash
npm install hypertube-nodejs-sdk
npm install --registry https://grft.dev/4b4e411f-60a0-4868-b8a6-46f5dee07448__free @graft/nuget-energypriceservice@1.2.0
\`\`\`

## 3. Create \`index.js\` and run

Create the file:

\`\`\`bash
touch index.js
\`\`\`

Open \`index.js\` in the file explorer and paste:

\`\`\`javascript
const { GraftConfig, MeterLogic } = require("@graft/nuget-energypriceservice");

GraftConfig.host = "wss://gc-d-ca-polc-demo-ecbe-01.blackgrass-d2c29aae.polandcentral.azurecontainerapps.io/ws";

(async () => {
  const consumption = await MeterLogic.NetConsumptionKWh(1000, 1150);
  console.log("Net consumption:", consumption);
})();
\`\`\`

Then run:

\`\`\`bash
node index.js
\`\`\`

You should see the net consumption value. \`MeterLogic.NetConsumptionKWh(...)\` is a remote call that reads like a local method — no HTTP, no serialization.`

const INSTRUCTIONS_PYTHON = `# Connect a Remote Service with Graftcode

Call a remote method as if it were local — strongly typed, no HTTP boilerplate.

---

## 1. Create a project folder

\`\`\`bash
mkdir py-energy-consumer
cd py-energy-consumer
\`\`\`

## 2. Install the Graft

\`\`\`bash
pip install hypertube-python-sdk
python -m pip install --extra-index-url https://grft.dev/simple/4b4e411f-60a0-4868-b8a6-46f5dee07448__free graft-nuget-energypriceservice==1.2.0
\`\`\`

## 3. Create \`main.py\` and run

Create the file:

\`\`\`bash
touch main.py
\`\`\`

Open \`main.py\` in the file explorer and paste:

\`\`\`python
import os
from graft_nuget_energypriceservice.graft.nuget.EnergyPriceService import GraftConfig
from graft_nuget_energypriceservice.meterlogic import MeterLogic

GraftConfig.host = "wss://gc-d-ca-polc-demo-ecbe-01.blackgrass-d2c29aae.polandcentral.azurecontainerapps.io/ws"

consumption = MeterLogic.netConsumptionKWh(1000, 1150)
print(f"Net consumption: {consumption}")
os._exit(0)
\`\`\`

Then run:

\`\`\`bash
python main.py
\`\`\`

You should see the net consumption value. \`MeterLogic.netConsumptionKWh(...)\` is a remote call that reads like a local method — no HTTP, no serialization.`

export const CHALLENGES: Challenge[] = [
  {
    id: 'graftcode-remote-service',
    title: 'Connect a Remote Service with Graftcode',
    difficulty: 'beginner',
    description:
      'Connect a backend service to another remote service using Graftcode — strongly typed, reads like a normal method call.',
    instructions: {
      csharp: INSTRUCTIONS_CSHARP,
      java: INSTRUCTIONS_JAVA,
      nodejs: INSTRUCTIONS_JAVASCRIPT,
      python: INSTRUCTIONS_PYTHON,
    },
    supportedLanguages: ['csharp', 'java', 'nodejs', 'python'],
    starterCode: {
      csharp: '',
      java: '',
      nodejs: '',
      python: '',
    },
    runCommand: {
      csharp: 'dotnet run',
      java: 'mvn compile exec:java "-Dexec.mainClass=energy.Main"',
      nodejs: 'node index.js',
      python: 'python main.py',
    },
    estimatedMinutes: 10,
    hints: [
      'Copy the commands from the instructions and paste them into the terminal',
      'The install command includes a custom registry/source URL — paste it exactly as shown',
      'Create files with touch or mkdir, then open them in the file explorer to paste code',
    ],
  },
]

export function getChallengeById(id: string): Challenge | undefined {
  return CHALLENGES.find((c) => c.id === id)
}
